import { useFetcher } from '@remix-run/react'
import { useEffect, useState } from 'react'
import {
  maskCPF,
  maskPhone,
  validateCPF,
  validateDate,
  validateName,
  validatePhone,
} from '../helpers/utilsValidation'
import InlineAlert from '../primitives/InlineAlert'
import PrivacyConsentGroup from './PrivacyConsentGroup'
import { Button, Loading, TextField } from '~/design-system/primitives'
import { useGlobalToast } from '~/hooks/useGlobalToast'
import { Save } from '~/icons'
import type { Profile } from '~/type/User'

interface FormNaturalPersonalDataProps {
  profile: Profile
  setEditPersonalData: (value: boolean) => void
  fetcherGetPersonalData: () => void
}

interface ResponsePersonalData {
  response: Profile
  success: boolean
}

export default function FormNaturalPersonalData({
  profile,
  setEditPersonalData,
  fetcherGetPersonalData,
}: FormNaturalPersonalDataProps) {
  const fetcher = useFetcher()
  const { show } = useGlobalToast()
  const [isLoading, setIsLoading] = useState(false)

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    document: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    pushTokens: [],
    politica_privacidade: false,
    swift_vc: false,
    swiftEvoceComunicacao: false,
  })

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({})

  const handleInputChange = (name: string, value: string, validate?: (value: string) => string | null) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
    const error = validate?.(value) ?? null
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const getErrorMessage = (name: keyof typeof formValues): string => {
    return (errors[name] ?? undefined) || (formValues[name] === '' ? 'Preencha este campo.' : '')
  }

  const handleCPFChange = (value: string) => {
    handleInputChange('document', maskCPF(value), validateCPF)
  }

  const handlePhoneChange = (value: string) => {
    handleInputChange('phone', maskPhone(value), validatePhone)
  }

  const handleOtpSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault()

    const formData = new FormData()
    formData.append('firstName', formValues.firstName)
    formData.append('lastName', formValues.lastName)
    formData.append('email', formValues.email)
    formData.append('document', formValues.document)
    formData.append('phone', formValues.phone)
    formData.append('dateOfBirth', formValues.dateOfBirth)
    formData.append('gender', formValues.gender)
    formData.append('pushTokens', JSON.stringify(formValues.pushTokens))
    formData.append('actionType', 'updateUserPf')
    formData.append('swift_vc', formValues.swift_vc ? 'true' : 'false')
    formData.append('politica_privacidade', formValues.politica_privacidade ? 'true' : 'false')
    formData.append('swiftEvoceComunicacao', formValues.swiftEvoceComunicacao ? 'true' : 'false')

    fetcher.submit(formData, { method: 'post', action: '/myaccount' })
    setIsLoading(true)
  }

  const isFormValid = () => {
    return (
      validateName(formValues.firstName) === null &&
      validateName(formValues.lastName) === null &&
      validateCPF(formValues.document) === null &&
      validatePhone(formValues.phone) === null &&
      validateDate(formValues.dateOfBirth) === undefined &&
      formValues.politica_privacidade === true
    )
  }

  useEffect(() => {
    if (profile) {
      const parsedConsents = Object.fromEntries(
        profile.consents.map((c: { key: string; value: boolean }) => [c.key, c.value]),
      )
      const isPrivacyPolicyAccepted = parsedConsents.politica_privacidade === true

      setFormValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        document: maskCPF(profile.document || ''),
        phone: maskPhone(profile.phone || ''),
        dateOfBirth: profile.dateOfBirth || '',
        gender: profile.gender || '',
        pushTokens: [],
        politica_privacidade: isPrivacyPolicyAccepted,
        swift_vc: isPrivacyPolicyAccepted ? parsedConsents.swift_vc || false : true,
        swiftEvoceComunicacao: isPrivacyPolicyAccepted ? parsedConsents.swiftEvoceComunicacao || false : true,
      })
    }
  }, [profile])

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      const data = fetcher.data as ResponsePersonalData

      if (data?.success) {
        show({ message: 'Dados alterados com sucesso', type: 'success' })
      } else {
        show({ message: 'Não foi possível alterar os dados', type: 'error' })
      }

      setTimeout(() => {
        setIsLoading(false)
        setEditPersonalData(false)
        fetcherGetPersonalData()
      }, 1000)
    }
  }, [fetcher])

  const consentValues = [
    ...(formValues.politica_privacidade ? ['politica_privacidade'] : []),
    ...(formValues.swift_vc ? ['swift_vc'] : []),
    ...(formValues.swiftEvoceComunicacao ? ['swiftEvoceComunicacao'] : []),
  ]

  const handleConsentChange = (updatedKeys: string[]) => {
    setFormValues((prev) => ({
      ...prev,
      politica_privacidade: updatedKeys.includes('politica_privacidade'),
      swift_vc: updatedKeys.includes('swift_vc'),
      swiftEvoceComunicacao: updatedKeys.includes('swiftEvoceComunicacao'),
    }))
  }

  return (
    <>
      {!isFormValid() && (
        <InlineAlert
          message="Preencha todos os campos obrigatórios e aceite a política de privacidade para salvar seus dados."
          title="Campos obrigatórios"
          variant="negative"
        />
      )}
      <fetcher.Form className="flex flex-col gap-4" onSubmit={handleOtpSubmit}>
        <div className="flex flex-col lg:flex-row gap-4">
          <TextField
            className="lg:w-1/3"
            id="email"
            label="Email:"
            name="email"
            type="email"
            value={formValues.email}
            isDisabled
          />
          <TextField
            className="lg:w-1/3"
            label="Primeiro nome *"
            name="firstName"
            type="text"
            value={formValues.firstName}
            isRequired
            onChange={(val) => handleInputChange('firstName', val)}
          />
          <TextField
            className="lg:w-1/3"
            label="Último Nome *"
            name="lastName"
            type="text"
            value={formValues.lastName}
            isRequired
            onChange={(val) => handleInputChange('lastName', val)}
          />
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <TextField
            className="lg:w-1/3"
            errorMessage={getErrorMessage('document')}
            label="CPF *"
            maxLength={14}
            name="document"
            type="tel"
            validate={validateCPF}
            value={formValues.document}
            isRequired
            onChange={handleCPFChange}
          />
          <TextField
            className="lg:w-1/3"
            errorMessage={getErrorMessage('phone')}
            label="Telefone celular *"
            maxLength={14}
            name="phone"
            type="tel"
            validate={validatePhone}
            value={formValues.phone}
            isRequired
            onChange={handlePhoneChange}
          />
          <TextField
            className="lg:w-1/3"
            label="Data de Nascimento"
            name="dateOfBirth"
            type="date"
            validate={validateDate}
            value={formValues.dateOfBirth}
            isRequired
            onChange={(val) => handleInputChange('dateOfBirth', val)}
          />
        </div>
        <PrivacyConsentGroup isCorporate={profile.isCorporate} isSubmitting={isLoading} value={consentValues} onChange={handleConsentChange} />
        <div className="flex lg:justify-end gap-2">
          <Button
            className="flex w-full lg:w-fit justify-center"
            color="primary"
            isDisabled={!isFormValid()}
            type="submit"
          >
            <Save className="fill-white mr-1 text-xl" />
            Salvar
          </Button>
        </div>
      </fetcher.Form>
      {isLoading && <Loading />}
    </>
  )
}
